"use client";

import { useGetAllBlockchainAddresses } from "@/subsystems/transfer/accounts/hooks/use-get-blockchain-addresses";
import { MINT_AND_INSTANT_LIQUIDITY_TYPES } from "@/subsystems/transfer/accounts/utils/customer-address-utils";
import { useGetAllBankAccounts } from "@/subsystems/transfer/bank-accounts/hooks/use-get-bank-accounts";

export type ConnectionType = "all" | "mintFiat" | "redeemFiat";
type TaskConfig = Partial<Record<TaskKey, boolean>>;

export type TaskKey = "hasAddedABankAccount" | "hasAddedABlockchainAddress";

type Tasks = Record<TaskKey, boolean>;

export function useGetStartedTaskList(connectionType: ConnectionType = "all") {
  // Query hooks — use filtered hooks so soft-deleted records are excluded
  const { bankAccounts } = useGetAllBankAccounts();
  const { blockchainAddresses } = useGetAllBlockchainAddresses({
    entitlementTypes: MINT_AND_INSTANT_LIQUIDITY_TYPES,
  });

  const isLoading = bankAccounts.isLoading || blockchainAddresses.isLoading;

  // Base tasks with their completion status
  const allTasks: Tasks = {
    hasAddedABankAccount: (bankAccounts.data?.length ?? 0) > 0,
    hasAddedABlockchainAddress: (blockchainAddresses.data?.length ?? 0) > 0,
  };

  // Task configurations for different connection types
  const taskConfigs: Record<ConnectionType, TaskConfig> = {
    all: {
      hasAddedABlockchainAddress: true,
      hasAddedABankAccount: true,
    },
    mintFiat: {
      // only requires blockchain address
      hasAddedABlockchainAddress: true,
      hasAddedABankAccount: false,
    },
    redeemFiat: {
      // only requires bank account
      hasAddedABlockchainAddress: false,
      hasAddedABankAccount: true,
    },
  };

  // Get relevant tasks based on connection type
  const relevantTasks = Object.entries(taskConfigs[connectionType]).reduce(
    (acc, [key, include]) => {
      if (include) {
        acc[key as TaskKey] = allTasks[key as TaskKey];
      }
      return acc;
    },
    {} as Partial<Tasks>,
  );

  // Calculate metrics
  const totalTasks = Object.keys(relevantTasks).length;
  const completedTasks = Object.values(relevantTasks).filter(Boolean).length;
  const remainingTasks = totalTasks - completedTasks;
  const percentageComplete = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 100;

  return {
    allTasksComplete: remainingTasks === 0,
    bankAccounts,
    blockchainAddresses,
    // Status
    isLoading,
    numberOfTasksLeft: remainingTasks,
    onboardingComplete: remainingTasks === 0,
    percentageComplete,
    tasks: relevantTasks,
    // Metrics
    totalNumberOfTasks: totalTasks,
  };
}
